import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { PushPreferences } from '../lib/api';

// In-app sound and vibration (issue #3). These belong to the device, not the
// account: a laptop at work and a phone at home want different answers.
export interface AlertSettings {
  messageSound: boolean;
  callRingtone: boolean;
  vibration: boolean;
}

export const alertSettingsAtom = atomWithStorage<AlertSettings>(
  'twinden:alertSettings',
  { messageSound: true, callRingtone: true, vibration: true },
  undefined,
  { getOnInit: true }
);

// Photos are resized before sending unless this device asks for originals
// (docs/photo-uploads.md, D136).
export type PhotoQuality = 'standard' | 'original';
export const photoQualityAtom = atomWithStorage<PhotoQuality>(
  'twinden:photoQuality',
  'standard',
  undefined,
  { getOnInit: true }
);

// Voice-note playback speed, 1×/1.5×/2×, remembered on this device
// (docs/chat-round-three.md, D221).
export const voiceSpeedAtom = atomWithStorage<number>(
  'twinden:voiceSpeed',
  1,
  undefined,
  { getOnInit: true }
);

// Indian date, time and number formats, or this device's own region
// (docs/indian-touches.md, D145).
export type Region = 'IN' | 'device';
export const regionAtom = atomWithStorage<Region>(
  'twinden:region',
  'IN',
  undefined,
  { getOnInit: true }
);

// Server-side notification preferences (M17), shared so mutes set in a chat,
// in Settings, or from a notification action agree everywhere. Null until
// loaded.
export const pushPreferencesAtom = atom<PushPreferences | null>(null);

// Aashirwad (docs/indian-touches.md, D280): your own blessing words and the
// quick 🙏 button, on this device only, like drafts. Empty words mean the
// default, "Jeete raho 🙏", in the app's language.
export const blessingWordsAtom = atomWithStorage<string>(
  'twinden:blessingWords',
  '',
  undefined,
  { getOnInit: true }
);
export const blessingButtonAtom = atomWithStorage<boolean>(
  'twinden:blessingButton',
  false,
  undefined,
  { getOnInit: true }
);

// Festival touches (docs/indian-touches.md, D312): the small drawing in the
// home header on festival days and the loader's diya flame. A device
// setting, on by default; never sent.
export const festivalTouchesAtom = atomWithStorage<boolean>(
  'twinden:festivalTouches',
  true,
  undefined,
  { getOnInit: true }
);
